import React, { useRef, useState } from 'react';

/**
 * Kanban générique, glisser-déposer HTML5 natif (pas de dépendance).
 *   colonnes   : [{ cle, libelle }]
 *   elements   : objets portant `id`, `statut`, `position`
 *   rendreCarte: (element) => contenu de la carte
 *   onOuvrir   : (element) => clic / Entrée sur une carte
 *   onDeplacer : (element, statut, position) => promesse ; le parent met à jour
 *   peutDeplacer: (element) => booléen
 * Sur mobile (pas de glisser au doigt), on change la colonne depuis la fiche.
 */
export default function Kanban({ colonnes, elements, rendreCarte, onOuvrir, onDeplacer, peutDeplacer = () => true, videTexte = 'Rien ici' }) {
  const [glisse, setGlisse] = useState(null);       // élément en cours de glissement
  const [cible, setCible] = useState(null);         // { statut, position }
  const refsColonnes = useRef({});

  const parColonne = (statut) => elements
    .filter((e) => e.statut === statut)
    .sort((a, b) => a.position - b.position || a.id - b.id);

  // Rang d'insertion d'après la position verticale du pointeur.
  function rangSous(statut, y) {
    const cartes = [...(refsColonnes.current[statut]?.querySelectorAll('[data-carte]') || [])]
      .filter((c) => Number(c.dataset.carte) !== glisse?.id);
    const i = cartes.findIndex((c) => { const r = c.getBoundingClientRect(); return y < r.top + r.height / 2; });
    return i === -1 ? cartes.length : i;
  }

  function deposer(statut) {
    const el = glisse;
    const position = cible?.position ?? 0;
    setGlisse(null);
    setCible(null);
    if (!el) return;
    const memePlace = el.statut === statut && parColonne(statut).findIndex((e) => e.id === el.id) === position;
    if (!memePlace) onDeplacer(el, statut, position);
  }

  return (
    <div className="kanban">
      {colonnes.map((col) => {
        const liste = parColonne(col.cle);
        const autres = liste.filter((e) => e.id !== glisse?.id).length;
        let rang = 0; // rang parmi les cartes hors carte glissée (celui du repère)
        const survol = glisse && cible?.statut === col.cle;
        return (
          <section
            key={col.cle}
            className={`colonne ${survol ? 'survol' : ''}`}
            aria-label={col.libelle}
            ref={(n) => { refsColonnes.current[col.cle] = n; }}
            onDragOver={(e) => {
              if (!glisse) return;
              e.preventDefault();
              const position = rangSous(col.cle, e.clientY);
              if (cible?.statut !== col.cle || cible?.position !== position) setCible({ statut: col.cle, position });
            }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setCible(null); }}
            onDrop={(e) => { e.preventDefault(); deposer(col.cle); }}
          >
            <div className="colonne-entete">
              <span>{col.libelle}</span>
              <span className="colonne-compte">{liste.length}</span>
            </div>
            {liste.map((el) => {
              const i = el.id === glisse?.id ? -1 : rang++;
              return (
              <React.Fragment key={el.id}>
                {survol && i >= 0 && cible.position === i && <div className="repere" />}
                <div
                  role="button"
                  tabIndex={0}
                  data-carte={el.id}
                  className={`carte-kanban ${glisse?.id === el.id ? 'glisse' : ''}`}
                  draggable={peutDeplacer(el)}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(el.id)); setGlisse(el); }}
                  onDragEnd={() => { setGlisse(null); setCible(null); }}
                  onClick={() => onOuvrir(el)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(el); } }}
                >
                  {rendreCarte(el)}
                </div>
              </React.Fragment>
              );
            })}
            {survol && cible.position >= autres && <div className="repere" />}
            {liste.length === 0 && !survol && <div className="colonne-vide">{videTexte}</div>}
          </section>
        );
      })}
    </div>
  );
}
